import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const PixelLogo = ({
    particleCount = 2500, // Increased default for larger areas
    color = '#456EF2',
    speed = 2.0,
    pixelSize = 2,
    images = [],
    className
}) => {
    const containerRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const loaderRef = useRef(null);
    const threeRef = useRef(null);
    const imageShapesRef = useRef([]);
    const mouseRef = useRef({ x: 9999, y: 9999 });
    const [isAssembling, setIsAssembling] = React.useState(true);

    // Preload images...
    useEffect(() => {
        if (images.length > 0) {
            const loadShapes = async () => {
                const results = await Promise.all(images.map(url => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.crossOrigin = "Anonymous";
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const gridSize = 100; // Increased resolution for better edge detail
                            canvas.width = gridSize;
                            canvas.height = gridSize;
                            ctx.drawImage(img, 0, 0, gridSize, gridSize);
                            const data = ctx.getImageData(0, 0, gridSize, gridSize).data;

                            const grid = new Array(gridSize * gridSize).fill(false);

                            // 1. First pass: Identify "shape" pixels
                            for (let y = 0; y < gridSize; y++) {
                                for (let x = 0; x < gridSize; x++) {
                                    const i = (y * gridSize + x) * 4;
                                    const a = data[i + 3];
                                    const r = data[i];
                                    const g = data[i + 1];
                                    const b = data[i + 2];
                                    // Consider pixel part of shape if opaque enough and dark enough
                                    const isOpaque = a > 128;
                                    const isDark = (r + g + b) / 3 < 200; // Relaxed darkness threshold

                                    if (isOpaque && isDark) {
                                        grid[y * gridSize + x] = true;
                                    }
                                }
                            }

                            const points = [];

                            // 2. Second pass: Classify and Sample
                            // We need to find Edges, Interior, and Halo (outside near edge)

                            const isShape = (x, y) => {
                                if (x < 0 || x >= gridSize || y < 0 || y >= gridSize) return false;
                                return grid[y * gridSize + x];
                            };

                            for (let y = 0; y < gridSize; y++) {
                                for (let x = 0; x < gridSize; x++) {
                                    const pIsShape = isShape(x, y);

                                    // Check neighbors
                                    let neighbors = 0;
                                    const neighborOffsets = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                                    neighborOffsets.forEach(([dx, dy]) => {
                                        if (isShape(x + dx, y + dy)) neighbors++;
                                    });

                                    const isEdge = pIsShape && neighbors < 4;
                                    const isInterior = pIsShape && neighbors === 4;
                                    // "Halo" is a non-shape pixel that has at least one shape neighbor
                                    // Expanding halo check slightly for "scattered around" effect
                                    let isHalo = false;
                                    if (!pIsShape) {
                                        // Check slightly wider area for halo
                                        for (let dy = -2; dy <= 2; dy++) {
                                            for (let dx = -2; dx <= 2; dx++) {
                                                if (Math.abs(dx) + Math.abs(dy) <= 2 && isShape(x + dx, y + dy)) {
                                                    isHalo = true;
                                                    break;
                                                }
                                            }
                                            if (isHalo) break;
                                        }
                                    }

                                    let shouldKeep = false;
                                    let type = 'interior'; // default

                                    if (isEdge) {
                                        // Very high chance for edges
                                        if (Math.random() < 0.95) {
                                            shouldKeep = true;
                                            type = 'edge';
                                        }
                                    } else if (isInterior) {
                                        // Lower chance for interior (fill)
                                        if (Math.random() < 0.15) {
                                            shouldKeep = true;
                                            type = 'interior';
                                        }
                                    } else if (isHalo) {
                                        // Scattered black dust around
                                        if (Math.random() < 0.05) {
                                            shouldKeep = true;
                                            type = 'halo';
                                        }
                                    }

                                    if (shouldKeep) {
                                        const nx = (x / gridSize) * 2 - 1;
                                        const ny = -((y / gridSize) * 2 - 1);
                                        // Add jitter based on type
                                        const jitter = type === 'halo' ? 0.03 : 0.005;
                                        const jx = (Math.random() - 0.5) * jitter;
                                        const jy = (Math.random() - 0.5) * jitter;

                                        points.push({
                                            x: nx * 0.8 + jx,
                                            y: ny * 0.8 + jy,
                                            type: type
                                        });
                                    }
                                }
                            }
                            resolve(points);
                        };
                        img.onerror = () => resolve([]);
                        img.src = url;
                    });
                }));
                imageShapesRef.current = results.filter(p => p.length > 0);
            };
            loadShapes();
        }
    }, [images]);

    useEffect(() => {
        if (!containerRef.current) return;

        // --- SETUP ---
        const scene = new THREE.Scene();

        // Initial setup
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
        camera.position.z = 1;

        const renderer = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
            powerPreference: "high-performance"
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        // Append renderer
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(renderer.domElement);

        // --- PARTICLES ---
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);

        const baseColorObj = new THREE.Color(color);

        for (let i = 0; i < particleCount; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 2;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 2;
            positions[i * 3 + 2] = 0;

            colors[i * 3] = baseColorObj.r;
            colors[i * 3 + 1] = baseColorObj.g;
            colors[i * 3 + 2] = baseColorObj.b;
        }
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
            vertexColors: true,
            size: pixelSize * (window.devicePixelRatio || 1),
            sizeAttenuation: false,
            transparent: true,
            opacity: 0.9,
        });

        const points = new THREE.Points(geometry, material);
        scene.add(points);

        // --- HELPERS ---
        // Grid point generator (same as before)
        const getGridPoint = (checkFn) => {
            const gridSize = 32; // Increased resolution
            const step = 2.0 / gridSize;
            let x, y, iter = 0;
            do {
                const ix = Math.floor(Math.random() * gridSize);
                const iy = Math.floor(Math.random() * gridSize);
                x = -1.0 + ix * step + step * 0.5;
                y = -1.0 + iy * step + step * 0.5;
                // Jitter
                x += (Math.random() - 0.5) * 0.01;
                y += (Math.random() - 0.5) * 0.01;
                iter++;
            } while (!checkFn(x, y) && iter < 1000);
            return { x: x * 0.8, y: y * 0.8, type: 'interior' };
        };

        const getSquare = (cnt) => {
            const arr = [];
            for (let i = 0; i < cnt; i++) arr.push(getGridPoint((x, y) => Math.abs(x) < 0.8 && Math.abs(y) < 0.8));
            return arr;
        }

        const getImageShape = (points) => (cnt) => {
            const arr = [];

            if (!points || points.length === 0) {
                // Fallback if no points
                return getSquare(cnt);
            }

            // All particles are shape particles now
            for (let i = 0; i < cnt; i++) {
                const sourceIndex = Math.floor(Math.random() * points.length);
                const sourcePoint = points[sourceIndex];

                const jitter = sourcePoint.type === 'edge' ? 0.002 : 0.01;
                const jitterX = (Math.random() - 0.5) * jitter;
                const jitterY = (Math.random() - 0.5) * jitter;

                arr.push({
                    x: sourcePoint.x + jitterX,
                    y: sourcePoint.y + jitterY,
                    type: sourcePoint.type,
                    isFloater: false
                });
            }

            return arr;
        };

        const builtInShapes = [getSquare];

        // --- ANIMATION STATE ---
        const pData = [];
        // Initialize particles
        for (let i = 0; i < particleCount; i++) {
            pData.push({
                x: positions[i * 3], y: positions[i * 3 + 1],
                vx: 0, vy: 0,
                tx: positions[i * 3], ty: positions[i * 3 + 1],
                r: baseColorObj.r, g: baseColorObj.g, b: baseColorObj.b,
                tr: baseColorObj.r, tg: baseColorObj.g, tb: baseColorObj.b, // Target colors
                delay: Math.random() * 0.4,
                speedMult: 0.8 + Math.random() * 0.4,
                phaseOffset: Math.random() * Math.PI * 2,
                orbitRadius: Math.random() * 0.02,
                isFloater: false
            });
        }

        let shapeIdx = 0;
        let phase = 'ASSEMBLE';
        let timeInPhase = 0;
        const PHASE_DURATIONS = { ASSEMBLE: 3.5, HOLD: 2.5 };
        let imagesLoadedRef = false;

        const assignTargetShape = (idx) => {
            const loadedImageGenerators = imageShapesRef.current.map(pts => getImageShape(pts));
            const hasImages = images && images.length > 0;
            const loaded = loadedImageGenerators.length > 0;

            if (hasImages && !loaded) {
                // Wait offscreen
                return [];
            }

            let allShapes = loaded ? loadedImageGenerators : builtInShapes;
            if (allShapes.length === 0) allShapes = builtInShapes;

            const generator = allShapes[idx % allShapes.length];
            const coords = generator(particleCount);

            for (let i = 0; i < particleCount; i++) {
                if (coords[i].isFloater) {
                    pData[i].isFloater = true;
                    // Floaters don't have a fixed tx/ty, they drift
                    // We'll just give them a random initial target to start transition
                    pData[i].tx = (Math.random() - 0.5) * 3;
                    pData[i].ty = (Math.random() - 0.5) * 3;
                } else {
                    pData[i].isFloater = false;
                    pData[i].tx = coords[i].x;
                    pData[i].ty = coords[i].y;
                }

                // Unified Color - all use base color
                pData[i].tr = baseColorObj.r;
                pData[i].tg = baseColorObj.g;
                pData[i].tb = baseColorObj.b;
            }
            return allShapes;
        };

        // Initialize with first available shape (image or square)
        assignTargetShape(0);

        const clock = new THREE.Clock();

        const animate = () => {
            const dt = Math.min(clock.getDelta(), 0.1) * speed;
            timeInPhase += dt;

            if (!imagesLoadedRef && imageShapesRef.current.length > 0) {
                imagesLoadedRef = true;
                shapeIdx = 0;

                // Snap to first image immediately
                const currentShapes = assignTargetShape(0);
                // Force positions to target so it appears instantly
                for (let i = 0; i < particleCount; i++) {
                    pData[i].x = pData[i].tx;
                    pData[i].y = pData[i].ty;
                    pData[i].r = pData[i].tr;
                    pData[i].g = pData[i].tg;
                    pData[i].b = pData[i].tb;
                    pData[i].vx = 0;
                    pData[i].vy = 0;
                    pData[i].delay = 0; // Reset delays
                }

                phase = 'HOLD';
                timeInPhase = 0;
            }

            // Phase Logic
            if (phase === 'ASSEMBLE') {
                if (timeInPhase > PHASE_DURATIONS.ASSEMBLE) {
                    phase = 'HOLD';
                    timeInPhase = 0;
                }
            } else if (phase === 'HOLD') {
                if (timeInPhase > PHASE_DURATIONS.HOLD) {
                    phase = 'ASSEMBLE';
                    timeInPhase = 0;

                    const loadedImageGenerators = imageShapesRef.current.map(pts => getImageShape(pts));
                    // Only cycle if we have multiple shapes/images
                    const allShapes = loadedImageGenerators.length > 0 ? loadedImageGenerators : builtInShapes;

                    if (allShapes.length > 1) {
                        shapeIdx = (shapeIdx + 1) % allShapes.length;
                        assignTargetShape(shapeIdx);
                        // Reset delays with random values for staggered animation
                        for (let i = 0; i < particleCount; i++) {
                            pData[i].delay = Math.random() * 0.5;
                        }
                    } else {
                        // Re-sample for dynamic effect even if same shape
                        assignTargetShape(shapeIdx);
                    }
                }
            }

            // Physics & Mouse Interaction
            // Mouse is in normalized device coordinates (NDC) [-1, 1] relative to center
            // But we need to account for aspect ratio if we want circular repulsion in world space
            // Camera covers vertical range [-1, 1] (if d=1). 
            // Correct logic depends on how we set up camera frustum in resize.

            // Get current aspect info from camera
            const aspect = (camera.right - camera.left) / (camera.top - camera.bottom);

            // Mouse world pos
            // Assuming camera at z=1 looking at z=0, ortho size is set by top/bottom/left/right
            // If we set top=1, bottom=-1, then vertical world size is 2.
            // MouseNDC y goes -1..1. WorldY = MouseNDC_Y * (top)

            // Map MouseRef coordinates (which are clientX/Y from event) to world space
            // We need screen dimensions to do this map. 
            // Better: update MouseRef to be NDC or World space in the event listener?
            // Let's do it here using our known camera bounds.

            // We'll trust mouseRef contains {mx, my} in Normalized Device Coords (-1 to +1)
            // adjusted for aspect ratio? No, NDC is square -1..1 usually, but world space is stretched.

            const mx = mouseRef.current.x; // NDC x
            const my = mouseRef.current.y; // NDC y (-1 bottom, 1 top)

            // Convert NDC to World
            // WorldX = NDC_X * camera.right (assuming symmetric -right to right)
            // WorldY = NDC_Y * camera.top

            const worldMouseX = mx * camera.right;
            const worldMouseY = my * camera.top;

            const repulseRadius = 0.5;
            const repulseForce = 5.0;

            for (let i = 0; i < particleCount; i++) {
                const p = pData[i];

                // Apply delay - particles start moving at different times
                if (p.delay > 0) {
                    p.delay -= dt;
                    continue;
                }

                // Color Lerp
                p.r += (p.tr - p.r) * 5.0 * dt;
                p.g += (p.tg - p.g) * 5.0 * dt;
                p.b += (p.tb - p.b) * 5.0 * dt;

                let ax = 0, ay = 0;



                // 1. Attraction to target with individual speed
                const baseForce = phase === 'ASSEMBLE' ? 4.0 : 1.0;
                const individualForce = baseForce * p.speedMult;
                ax = (p.tx - p.x) * individualForce;
                ay = (p.ty - p.y) * individualForce;

                // 2. Add orbital/wave motion for more organic feel (reduced during ASSEMBLE)
                const waveTime = timeInPhase + p.phaseOffset;
                const motionScale = phase === 'ASSEMBLE' ? 0.15 : 0.5;
                const orbitalX = Math.cos(waveTime * 2) * p.orbitRadius;
                const orbitalY = Math.sin(waveTime * 2) * p.orbitRadius;
                ax += orbitalX * motionScale;
                ay += orbitalY * motionScale;

                // 3. Add Perlin-like noise for chaotic movement (reduced during ASSEMBLE)
                const noiseScale = phase === 'ASSEMBLE' ? 0.3 : 1.0;
                ax += Math.sin(waveTime * 3 + i * 0.1) * 0.02 * noiseScale;
                ay += Math.cos(waveTime * 2.5 + i * 0.15) * 0.02 * noiseScale;

                // 4. Mouse Repulsion
                const dx = p.x - worldMouseX;
                const dy = p.y - worldMouseY;
                const distSq = dx * dx + dy * dy;
                const dist = Math.sqrt(distSq);

                if (dist < repulseRadius) {
                    const f = (1.0 - dist / repulseRadius) * repulseForce;
                    ax += (dx / dist) * f;
                    ay += (dy / dist) * f;
                }

                // 5. Integration with individual characteristics
                p.vx += ax * dt * p.speedMult;
                p.vy += ay * dt * p.speedMult;

                // Damping (slightly varied per particle)
                const damping = 0.88 + Math.sin(i) * 0.04; // 0.84-0.92 range
                p.vx *= damping;
                p.vy *= damping;

                p.x += p.vx * dt;
                p.y += p.vy * dt;
            }

            // Update positions
            const posAttr = geometry.attributes.position.array;
            const colAttr = geometry.attributes.color.array;
            for (let i = 0; i < particleCount; i++) {
                posAttr[i * 3] = pData[i].x;
                posAttr[i * 3 + 1] = pData[i].y;

                colAttr[i * 3] = pData[i].r; // Just using r,g,b from state
                colAttr[i * 3 + 1] = pData[i].g;
                colAttr[i * 3 + 2] = pData[i].b;
            }
            geometry.attributes.position.needsUpdate = true;
            geometry.attributes.color.needsUpdate = true;

            renderer.render(scene, camera);
            threeRef.current = requestAnimationFrame(animate);
        };

        threeRef.current = requestAnimationFrame(animate);

        // --- RESIZE & MOUSE HANDLERS ---
        const handleResize = () => {
            if (!containerRef.current) return;
            const w = containerRef.current.clientWidth;
            const h = containerRef.current.clientHeight;

            renderer.setSize(w, h);

            // Adjust camera to maintain constant vertical coverage height
            // Let's say we want vertical world height to be approx 2.4 units (-1.2 to 1.2)
            // This ensures our logo (approx -0.8 to 0.8) fits vertically with margin.
            // Reduce targetHeight to zoom in (smaller view height = larger content)
            const targetHeight = 1.5;
            const aspect = w / h;
            const targetWidth = targetHeight * aspect;

            camera.left = -targetWidth / 2;
            camera.right = targetWidth / 2;
            camera.top = targetHeight / 2;
            camera.bottom = -targetHeight / 2;

            camera.updateProjectionMatrix();
        };

        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            // Calculate NDC (-1 to +1)
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1; // +1 at top

            mouseRef.current = { x, y };
        };

        const handleMouseLeave = () => {
            mouseRef.current = { x: 9999, y: 9999 };
        };

        const resizeObserver = new ResizeObserver(() => handleResize());
        resizeObserver.observe(containerRef.current);

        // Initial resize
        setTimeout(handleResize, 0);

        // Events
        // Attach to window or container? Container is safer for specific interaction
        containerRef.current.addEventListener('mousemove', handleMouseMove);
        containerRef.current.addEventListener('mouseleave', handleMouseLeave);

        // Cleanup
        return () => {
            cancelAnimationFrame(threeRef.current);
            resizeObserver.disconnect();
            if (containerRef.current) {
                containerRef.current.removeEventListener('mousemove', handleMouseMove);
                containerRef.current.removeEventListener('mouseleave', handleMouseLeave);
                if (renderer.domElement) containerRef.current.removeChild(renderer.domElement);
            }
            geometry.dispose();
            material.dispose();
            renderer.dispose();
        };

    }, [particleCount, color, speed, pixelSize]); // Re-init on simple prop changes

    // Use flex-grow or similar if in a flex container, but minHeight ensures visibility
    return <div ref={containerRef} className={className} style={{ width: '100%', height: '100%', minHeight: '500px' }} />;
};

export default PixelLogo;
